import { createPortal } from "react-dom";
import { useLayer } from "@/lib/layer-stack";

/** Lightweight confirmation modal for destructive bulk ops.
 *
 *  Use case: user clicks "Pause" with 32 campaigns selected — the
 *  Promise.all fanout takes ~1 second and there's no way to take it
 *  back. Adding a confirm step kills the silent-mass-mutation class
 *  of mistakes that previously left 32 prod campaigns paused without
 *  any notification (incident 2026-05-04).
 *
 *  Renders as a portal to <body> with a darker overlay than the
 *  backdrop so it visually outranks any underlying modal/popover. */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // Esc closes the dialog through the layer stack — keyboard users
  // shouldn't be trapped, and only the top layer answers.
  useLayer(open, onCancel);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200, // above .bk-overlay (100) so we can stack on top of bulk-edit modal too
        background: "rgba(14,18,32,.55)",
        display: "grid",
        placeItems: "center",
        padding: 32,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(440px, 100% - 32px)",
          background: "var(--c-surface)",
          border: "1px solid var(--c-border)",
          borderRadius: 12,
          boxShadow: "0 24px 60px -20px rgba(14,18,32,.4), 0 4px 12px rgba(14,18,32,.2)",
          padding: "20px 22px",
        }}
      >
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: "var(--c-text)",
            letterSpacing: "-0.01em",
            marginBottom: 8,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 13.5,
            color: "var(--c-text-2)",
            lineHeight: 1.45,
            marginBottom: 18,
          }}
        >
          {body}
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn" onClick={onCancel} type="button">
            {cancelLabel}
          </button>
          <button
            className={destructive ? "btn destructive" : "btn primary"}
            onClick={onConfirm}
            type="button"
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
